// TypeScript port of api-recorder-extension/src/playwright-converter.js
// Converts Gherkin scenarios into a Playwright TypeScript .spec.ts file.

import type { ParsedGherkinCase } from './aiOutputParser';

// ── Shared assertion types ────────────────────────────────────────────────────

export interface StepAssertion {
  type: 'status' | 'hasField' | 'fieldEquals' | 'emptyArray' | 'responseTime' | 'responseHeader';
  value?: number | string;
  field?: string;
  ms?: number;
  header?: string;
}

export interface StepParseResult {
  baseURL: string | null;
  headers: Record<string, string>;
  method: string | null;
  path: string | null;
  body: string | null;
  assertions: StepAssertion[];
}

// ── Step line parser ──────────────────────────────────────────────────────────

export function parseSteps(lines: string[]): StepParseResult {
  const result: StepParseResult = {
    baseURL: null,
    headers: {},
    method: null,
    path: null,
    body: null,
    assertions: [],
  };

  let inDocString = false;
  const docStringLines: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed === '"""') {
      if (inDocString) {
        inDocString = false;
        result.body = docStringLines.join('\n').trim();
        docStringLines.length = 0;
      } else {
        inDocString = true;
      }
      continue;
    }
    if (inDocString) { docStringLines.push(trimmed); continue; }

    let m: RegExpMatchArray | null;

    m = trimmed.match(/^(?:Given|And|But)\s+the base URL is "([^"]+)"/i);
    if (m) { result.baseURL = m[1]; continue; }

    m = trimmed.match(/^(?:Given|And|But)\s+the request header "([^"]+)" is "([^"]+)"/i);
    if (m) { result.headers[m[1]] = m[2]; continue; }

    m = trimmed.match(/^(?:When|And)\s+I send an? (\w+) request to "([^"]+)" with body:/i);
    if (m) { result.method = m[1].toLowerCase(); result.path = m[2]; continue; }

    m = trimmed.match(/^(?:When|And)\s+I send an? (\w+) request to "([^"]+)"/i);
    if (m) { result.method = m[1].toLowerCase(); result.path = m[2]; continue; }

    m = trimmed.match(/^(?:Then|And)\s+the response status code should be (\d+)/i);
    if (m) { result.assertions.push({ type: 'status', value: parseInt(m[1], 10) }); continue; }

    m = trimmed.match(/^(?:Then|And)\s+the response body should contain (?:the )?field "([^"]+)"/i);
    if (m) { result.assertions.push({ type: 'hasField', field: m[1] }); continue; }

    m = trimmed.match(/^(?:Then|And)\s+the response body (?:field )?"([^"]+)" should (?:equal|be) "([^"]*)"/i);
    if (m) { result.assertions.push({ type: 'fieldEquals', field: m[1], value: m[2] }); continue; }

    m = trimmed.match(/^(?:Then|And)\s+the response body should be an? empty array/i);
    if (m) { result.assertions.push({ type: 'emptyArray' }); continue; }

    m = trimmed.match(/^(?:Then|And)\s+the response time should be less than (\d+) milliseconds?/i);
    if (m) { result.assertions.push({ type: 'responseTime', ms: parseInt(m[1], 10) }); continue; }

    m = trimmed.match(/^(?:Then|And)\s+the response header "([^"]+)" should contain "([^"]+)"/i);
    if (m) { result.assertions.push({ type: 'responseHeader', header: m[1].toLowerCase(), value: m[2] }); continue; }
  }

  return result;
}

// ── Background parser ─────────────────────────────────────────────────────────

export function parseBackground(rawText: string): { baseURL: string | null; headers: Record<string, string> } {
  if (!rawText) return { baseURL: null, headers: {} };

  const bgLines: string[] = [];
  let inBg = false;

  for (const line of rawText.split('\n')) {
    const trimmed = line.trim();
    if (trimmed.startsWith('Background:')) { inBg = true; continue; }
    if (inBg) {
      if (trimmed.startsWith('Scenario') || trimmed.startsWith('Feature') || trimmed.startsWith('@')) break;
      if (trimmed && !trimmed.startsWith('#')) bgLines.push(line);
    }
  }

  const bg = parseSteps(bgLines);
  return { baseURL: bg.baseURL, headers: bg.headers };
}

// ── Single test block generator ───────────────────────────────────────────────

function scenarioToTest(tc: ParsedGherkinCase, bg: { baseURL: string | null; headers: Record<string, string> }): string {
  const steps = parseSteps(tc.lines);

  const baseURL = steps.baseURL || bg.baseURL || '';
  const headers = { ...bg.headers, ...steps.headers };
  const method  = steps.method || 'get';
  const path    = steps.path || '/';

  const urlExpr = path.startsWith('http') ? `'${path}'` : `\`\${BASE_URL}${path}\``;

  const optionParts: string[] = [];

  if (Object.keys(headers).length > 0) {
    const hLines = Object.entries(headers)
      .map(([k, v]) => `        '${k}': '${v}'`)
      .join(',\n');
    optionParts.push(`      headers: {\n${hLines}\n      }`);
  }

  if (steps.body !== null) {
    try {
      const parsed = JSON.parse(steps.body);
      const pretty = JSON.stringify(parsed, null, 2).replace(/\n/g, '\n      ');
      optionParts.push(`      data: ${pretty}`);
    } catch {
      optionParts.push(`      data: \`${steps.body.replace(/`/g, '\\`')}\``);
    }
  }

  const needsTimer = steps.assertions.some(a => a.type === 'responseTime');
  const needsBody  = steps.assertions.some(a => ['hasField', 'fieldEquals', 'emptyArray'].includes(a.type));

  const out: string[] = [];
  const label = `${tc.id} — ${tc.scenarioName.replace(/'/g, "\\'")}`;
  out.push(`  test('${label}', async ({ request }) => {`);

  if (needsTimer) out.push(`    const start = Date.now();`);

  const optStr = optionParts.length ? `, {\n${optionParts.join(',\n')}\n    }` : '';
  out.push(`    const response = await request.${method}(${urlExpr}${optStr});`);

  if (needsTimer) out.push(`    const elapsed = Date.now() - start;`);
  if (needsBody)  out.push(`    const body = await response.json().catch(() => null);`);

  for (const a of steps.assertions) {
    switch (a.type) {
      case 'status':
        out.push(`    expect(response.status()).toBe(${a.value});`);
        break;
      case 'hasField':
        out.push(`    expect(body).toHaveProperty('${a.field}');`);
        break;
      case 'fieldEquals':
        out.push(`    expect(body['${a.field}']).toBe('${a.value}');`);
        break;
      case 'emptyArray':
        out.push(`    expect(Array.isArray(body)).toBe(true);`);
        out.push(`    expect(body).toHaveLength(0);`);
        break;
      case 'responseTime':
        out.push(`    expect(elapsed).toBeLessThan(${a.ms});`);
        break;
      case 'responseHeader':
        out.push(`    expect(response.headers()['${a.header}']).toContain('${a.value}');`);
        break;
    }
  }

  out.push(`  });`);
  return out.join('\n');
}

// ── Public API ────────────────────────────────────────────────────────────────

export function scenariosToPlaywright(cases: ParsedGherkinCase[], rawText: string, featureName: string): string {
  if (!cases || cases.length === 0) return '';

  const name    = featureName || (cases[0] && cases[0].featureName) || 'API Tests';
  const bg      = parseBackground(rawText);
  const baseURL = bg.baseURL || 'https://your-api-base-url.com';
  const now     = new Date().toISOString().slice(0, 19).replace('T', ' ');

  const testBlocks = cases.map(tc => scenarioToTest(tc, bg)).join('\n\n');

  return [
    `import { test, expect } from '@playwright/test';`,
    ``,
    `// Generated by FINT Portal — ${now}`,
    `// Feature: ${name}`,
    `//`,
    `// Prerequisites:`,
    `//   npm init playwright@latest   (first time)`,
    `//   npx playwright install chromium`,
    `//`,
    `// Run all tests:   npx playwright test ${safeFilePart(name)}.spec.ts`,
    `// Run one test:    npx playwright test -g "TC_API_001"`,
    `// View report:     npx playwright show-report`,
    ``,
    `const BASE_URL = '${baseURL}';`,
    ``,
    `test.describe('${name.replace(/'/g, "\\'")}', () => {`,
    testBlocks,
    `});`,
    ``,
  ].join('\n');
}

function safeFilePart(name: string): string {
  return (name || 'api_tests')
    .replace(/[^a-zA-Z0-9_\-]/g, '_')
    .replace(/_+/g, '_')
    .toLowerCase()
    .slice(0, 40);
}
